import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CollaboratorForm,
  createCollaborator,
  EMPTY_COLLABORATOR_FORM,
  toCollaboratorForm,
  updateCollaborator,
} from "../../../api/collaborator";
import { AccountWithoutPassword } from "../../../generated/types";
import { VALIDATION_RULES, validateForm } from "../../../utils/formValidation";
import { Button } from "../../common/Button";
import { FormInput } from "../../common/FormLayout";
import { Modal, ModalActions } from "../../common/Modal";

export type CollaboratorModalTarget =
  | { mode: "create" }
  | { mode: "edit"; account: AccountWithoutPassword };

type CollaboratorModalProps = {
  target: CollaboratorModalTarget;
  owner: AccountWithoutPassword;
  onClose: () => void;
  onSuccess: () => void;
};

/**
 * Création d'un compte dans l'entreprise du créateur (USERS envoie le mail d'activation),
 * ou modification d'un collaborateur existant. Monté à l'ouverture : le formulaire part
 * toujours des valeurs de la cible.
 */
export const CollaboratorModal = ({
  target,
  owner,
  onClose,
  onSuccess,
}: CollaboratorModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CollaboratorForm>(
    target.mode === "edit"
      ? toCollaboratorForm(target.account)
      : EMPTY_COLLABORATOR_FORM
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors(({ [name]: _cleared, ...rest }) => rest);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const validationErrors = validateForm(form, {
      last_name: [VALIDATION_RULES.minLength(2, t("register-error-name"))],
      first_name: [
        VALIDATION_RULES.minLength(2, t("register-error-firstname")),
      ],
      email: [VALIDATION_RULES.email(t("register-error-email"))],
    });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    const result =
      target.mode === "edit"
        ? await updateCollaborator(form, target.account)
        : await createCollaborator(form, owner);
    setSubmitting(false);
    if (result) {
      setError(t(result));
      return;
    }
    onSuccess();
    onClose();
  };

  const isEdit = target.mode === "edit";
  // i18n.t("collaborator-edit-title")
  // i18n.t("collaborator-create-submit")

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t(
        isEdit ? "collaborator-edit-title" : "collaborator-create-title"
      )}
      icon="user-group"
      maxWidth="md"
    >
      <form onSubmit={submit} className="space-y-4">
        {!isEdit && (
          <p className="text-tertiary">{t("collaborator-create-text")}</p>
        )}
        {error && (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm">
            {error}
          </div>
        )}
        <FormInput
          label={t("user-name")}
          name="last_name"
          value={form.last_name}
          onChange={handleChange}
          required
          error={errors.last_name}
        />
        <FormInput
          label={t("user-firstname")}
          name="first_name"
          value={form.first_name}
          onChange={handleChange}
          required
          error={errors.first_name}
        />
        <FormInput
          label={t("user-email")}
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          required
          error={errors.email}
          lockedHint={isEdit ? t("collaborator-email-locked-hint") : undefined}
        />
        <FormInput
          label={t("user-phone-mobile")}
          type="tel"
          name="phone_mobile"
          value={form.phone_mobile}
          onChange={handleChange}
        />
        <FormInput
          label={t("user-phone-fixe")}
          type="tel"
          name="phone_fixe"
          value={form.phone_fixe}
          onChange={handleChange}
        />
        <ModalActions>
          <Button type="button" variant="secondary" onClick={onClose}>
            {t("button-cancel")}
          </Button>
          <Button type="submit" disabled={submitting}>
            {t(isEdit ? "button-save" : "collaborator-create-submit")}
          </Button>
        </ModalActions>
      </form>
    </Modal>
  );
};
